import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '@/components/LocalizedLink';
import { ArrowLeft, Package, CheckCircle, Clock, Box } from 'lucide-react';
import { useApiData } from '@/hooks/useApiData';
import PhotoGallery from '@/components/product/PhotoGallery';
import VideoSection from '@/components/product/VideoSection';
import ProductSummary from '@/components/product/ProductSummary';
import { FullSpecs, OtherProducts } from '@/components/product/ProductExtras';

const NO_ITEMS = [];

const hasValue = (v) => Boolean(v) && String(v).trim() !== '';

function buildView(product) {
  if (!product) return null;
  const specs = product.specs || {};
  const basePhotos = product.photos?.length > 0 ? product.photos : [product.image].filter(Boolean);
  return {
    specs,
    photos: [...new Set(basePhotos)],
    videos: [...new Set(product.videos || [])],
    specEntries: Object.entries(specs).filter(([, v]) => hasValue(v)),
    keySpecs: [
      { icon: CheckCircle, label: 'Certification', value: specs.certification },
      { icon: Clock, label: 'Shelf Life', value: specs.shelf_life },
      { icon: Box, label: 'MOQ', value: specs.moq },
      { icon: Package, label: 'Moisture', value: specs.moisture },
    ].filter(item => hasValue(item.value)),
  };
}

function PageMessage({ children }) {
  return <div className="min-h-screen pt-32 flex flex-col items-center justify-center gap-4">{children}</div>;
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const productReq = useApiData(`/api/products/${id}`, null);
  const allReq = useApiData('/api/products', NO_ITEMS);
  const product = productReq.data;
  const view = useMemo(() => buildView(product), [product]);
  const others = useMemo(() => allReq.data.filter(p => p.id !== id), [allReq.data, id]);

  if (productReq.loading || allReq.loading) {
    return <PageMessage><div className="animate-pulse text-earth/30 font-sans">Loading product...</div></PageMessage>;
  }
  if (!product) {
    return (
      <PageMessage>
        <p className="text-earth/50 font-sans text-lg">Product not found</p>
        <Link to="/products" className="text-saffron font-sans font-semibold text-sm">Back to Products</Link>
      </PageMessage>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <section className="pt-28 pb-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Link to="/products" className="inline-flex items-center gap-2 text-earth/50 hover:text-saffron font-sans text-sm transition-colors" data-testid="back-to-products">
          <ArrowLeft size={14} /> Back to Products
        </Link>
      </section>
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <PhotoGallery key={product.id} photos={view.photos} productName={product.name} color={product.color} />
            <ProductSummary product={product} keySpecs={view.keySpecs} />
          </div>
        </div>
      </section>
      <VideoSection key={`videos-${product.id}`} videos={view.videos} productName={product.name} />
      <FullSpecs entries={view.specEntries} />
      <OtherProducts products={others} />
    </div>
  );
}
